/**
 * AddressFields — structured home-address inputs for the booking form.
 *
 * Andy travels to the client's home, so we capture a structured address.
 * Fully self-contained: no external geocoder. The native `autoComplete`
 * attributes let the browser offer the visitor their own saved addresses
 * (which needs no third-party service).
 *
 * `value` is the address object the parent owns:
 *   { line1, line2, city, state, postal_code, formatted, lat, lon }
 * Only line1/line2/city/state/postal_code are edited here; formatted/lat/lon
 * are left as-is (server treats them as optional).
 * `onChange(next)` receives the full updated object.
 */

import React from 'react';

export default function AddressFields({ value, onChange, disabled }) {
  const set = (patch) => onChange({ ...value, ...patch });

  return (
    <div>
      <label style={lbl} htmlFor="addr-line1">
        Street address
      </label>
      <input
        id="addr-line1"
        style={input}
        value={value.line1 || ''}
        onChange={(e) => set({ line1: e.target.value })}
        autoComplete="address-line1"
        placeholder="123 Main St"
        disabled={disabled}
      />

      <label style={lbl} htmlFor="addr-line2">
        Apt / unit <span style={optional}>(optional)</span>
      </label>
      <input
        id="addr-line2"
        style={input}
        value={value.line2 || ''}
        onChange={(e) => set({ line2: e.target.value })}
        autoComplete="address-line2"
        disabled={disabled}
      />

      <div style={row}>
        <div style={{ flex: 2 }}>
          <label style={lbl} htmlFor="addr-city">
            City
          </label>
          <input
            id="addr-city"
            style={input}
            value={value.city || ''}
            onChange={(e) => set({ city: e.target.value })}
            autoComplete="address-level2"
            disabled={disabled}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={lbl} htmlFor="addr-state">
            State
          </label>
          <input
            id="addr-state"
            style={input}
            value={value.state || ''}
            onChange={(e) => set({ state: e.target.value })}
            autoComplete="address-level1"
            disabled={disabled}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={lbl} htmlFor="addr-zip">
            ZIP
          </label>
          <input
            id="addr-zip"
            style={input}
            value={value.postal_code || ''}
            onChange={(e) => set({ postal_code: e.target.value })}
            autoComplete="postal-code"
            inputMode="numeric"
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
}

const lbl = { display: 'block', fontWeight: 600, margin: '1rem 0 0.35rem' };
const optional = { fontWeight: 400, color: 'rgba(0,0,0,0.45)' };
const input = {
  width: '100%',
  padding: '0.65rem 0.85rem',
  fontSize: '1rem',
  border: '1px solid rgba(0,0,0,0.15)',
  borderRadius: 8,
  boxSizing: 'border-box',
};
const row = { display: 'flex', gap: 12, flexWrap: 'wrap' };
